import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Mic, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import { ObservationCard } from "@/components/app/RealisationObservations";
import {
  activeHypotheses, alternativeDetails, alternatives, answeredAlternatives, confidenceChange, decidesBetween, hasWins,
  hypothesisResponsePath, insightItems, leadParagraph, openAlternatives, otherQuestions, portraitParagraphs,
  primaryQuestion, revisedSegments, speechSupported,
} from "@/lib/currentReadModel";

const LEGACY_NOTE =
  "Inferred from a non-default answer saved in your profile earlier, not an answer you've confirmed since.";
const PROSE = "text-base sm:text-[1.125rem] leading-[1.75] text-stone-800";
const KICKER = "text-xs font-underground-sc tracking-[0.16em]";
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2";
const BTN = "inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-base font-medium " +
  "motion-safe:transition-colors disabled:opacity-60 " + FOCUS;

function Standing({ item }) {
  const stated = item.stated;
  return (
    <span className="text-sm text-stone-500" data-testid={"current-read-standing-" + item.id}>
      {stated ? "You told us" : "We think"}
      {!stated && item.confidence ? " · " + (item.confidence_label ? item.confidence_label + ", " : "") + item.confidence + "% evidence confidence" : ""}
    </span>
  );
}

function Notes({ item, id }) {
  return (
    <>
      {item.answer_basis === "legacy_nondefault" && (
        <p className="mt-2 text-sm italic text-amber-800" data-testid={"current-read-legacy-" + id}>{LEGACY_NOTE}</p>
      )}
      {item.learning_note && (
        <p className="mt-2 text-sm text-sky-900" data-testid={"current-read-learning-note-" + id}>{item.learning_note}</p>
      )}
    </>
  );
}

function Disclosure({ id, label, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={"inline-flex min-h-10 items-center gap-1 text-sm font-medium text-stone-500 underline-offset-4 hover:text-stone-900 hover:underline " + FOCUS}
        data-testid={testId + "-toggle-" + id}>
        {label}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-2 border-l-2 border-stone-200 pl-4 text-sm leading-relaxed text-stone-600 break-words [overflow-wrap:anywhere]" data-testid={testId + "-" + id}>
          {children}
        </div>
      )}
    </div>
  );
}

function Lines({ evidence, lines }) {
  return (
    <>
      {evidence && <p>{evidence}</p>}
      {lines?.length > 0 && <ul className="mt-1.5 space-y-1">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>}
    </>
  );
}

function Revised({ text, passages, id }) {
  return revisedSegments(text, passages).map((s, i) => (s.revised ? (
    <mark key={i} className="rounded-sm bg-amber-100 px-0.5 text-stone-900 [box-decoration-break:clone] motion-safe:transition-colors"
      data-testid={"current-read-revised-" + id}>{s.text}</mark>
  ) : <span key={i}>{s.text}</span>));
}

function WhatChanged({ text, id }) {
  if (!text) return null;
  return (
    <p className="mt-3 border-l-2 border-amber-400 pl-4 text-base text-amber-950 motion-safe:transition-colors" role="status" aria-live="polite"
      data-testid={"current-read-what-changed-" + id}>
      <span className="font-semibold">What your answer changed: </span>{text}
    </p>
  );
}

// the thesis is already the headline; don't make the reader read it twice
const withoutThesis = (text, thesis) =>
  (thesis && String(text || "").startsWith(thesis) ? String(text).slice(thesis.length).trim() : text) || text;

function Lead({ read }) {
  const big = read.big_picture;
  const lead = leadParagraph(read);
  return (
    <header className="max-w-[44rem]">
      <p className="font-editorial text-3xl sm:text-5xl leading-[1.15] text-stone-900" data-testid="current-read-thesis">
        {big?.thesis || read.summary}
      </p>
      {read.overall_confidence && (
        <p className="mt-4 text-sm text-stone-500" data-testid="current-read-overall">
          Overall, {String(read.overall_confidence_label || "our read").toLowerCase()} · {read.overall_confidence}% evidence confidence
        </p>
      )}
      {lead && (
        <div className="mt-6" data-testid="current-read-lead">
          <p className={PROSE}>{withoutThesis(lead.text, big?.thesis)}</p>
          <Notes item={lead} id="lead" />
          {lead.evidence_lines?.length > 0 && (
            <Disclosure id="lead" label="Why we think this" testId="current-read-evidence"><Lines lines={lead.evidence_lines} /></Disclosure>
          )}
        </div>
      )}
    </header>
  );
}

// A stated answer the portrait is built on can always be changed in place.
function ChangeAnswer({ h, onRespond, onResolved }) {
  const [open, setOpen] = useState(false);
  if (!h) return null;
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={"change-answer-" + h.hypothesis_type}
        className={"inline-flex min-h-10 items-center gap-1 text-base font-medium text-stone-600 hover:text-stone-900 " + FOCUS}>
        <Pencil className="h-4 w-4" /> {open ? "Keep my answer" : "Change this answer"}
      </button>
      {open && (
        <div className="mt-4">
          <HypothesisCard h={h} onRespond={onRespond} onResolved={() => { setOpen(false); onResolved?.(); }} />
        </div>
      )}
    </div>
  );
}

function Portrait({ read, onRespond, onResolved }) {
  const paras = portraitParagraphs(read);
  const themesAnswered = (read?.learning?.answered || []).find((h) => h.hypothesis_type === "study_themes");
  if (!paras.length) return null;
  return (
    <div className="max-w-[44rem] space-y-10" data-testid="current-read-portrait">
      {paras.map((p) => {
        const id = "p-" + p.theme;
        return (
          <section key={p.theme} aria-labelledby={"cr-h-" + p.theme} data-testid={"current-read-paragraph-" + p.theme}>
            <h3 id={"cr-h-" + p.theme} className="font-editorial text-2xl sm:text-[1.75rem] leading-snug text-stone-900">{p.title}</h3>
            <div className="mt-1"><Standing item={{ ...p, id }} /></div>
            <p className={"mt-3 " + PROSE}><Revised text={p.text} passages={p.revised_passages} id={p.theme} /></p>
            <WhatChanged text={p.what_changed} id={id} />
            {p.theme === "study_themes" && <ChangeAnswer h={themesAnswered} onRespond={onRespond} onResolved={onResolved} />}
            <Notes item={p} id={id} />
            {p.evidence_lines?.length > 0 && (
              <Disclosure id={id} label="Why we think this" testId="current-read-evidence"><Lines lines={p.evidence_lines} /></Disclosure>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Learned({ read }) {
  const items = insightItems(read);
  if (!items.length && !read.less_sure?.length) return null;
  return (
    <div className="max-w-[44rem]" data-testid="current-read-learning">
      <h3 className="font-editorial text-2xl sm:text-3xl text-stone-900">Things we think we've learned</h3>
      <p className="mt-2 text-base text-stone-500">Our strongest current inferences. None of these are confirmed until you say so.</p>
      <div className="mt-6 divide-y divide-stone-200 border-y border-stone-200">
        {items.map((i) => (
          <article key={i.id} className="min-w-0 py-6" data-testid={"current-read-learned-" + i.id}>
            <h4 className="font-editorial text-xl leading-snug text-stone-900 break-words">
              {i.title}
              {i.surprise && <span className="ml-2 align-middle text-sm font-sans text-amber-800">· worth noticing</span>}
            </h4>
            <div className="mt-1"><Standing item={i} /></div>
            <p className={"mt-2 " + PROSE}>{i.text}</p>
            <WhatChanged text={i.what_changed} id={i.id} />
            {i.student_signal && <p className="mt-2 text-base text-[#4C1D95]">{i.student_signal}</p>}
            <Notes item={i} id={i.id} />
            {(i.evidence || i.lines?.length > 0) && (
              <Disclosure id={i.id} label="Why we think this" testId="current-read-evidence"><Lines evidence={i.evidence} lines={i.lines} /></Disclosure>
            )}
          </article>
        ))}
      </div>
      {read.less_sure?.length > 0 && (
        <div className="mt-8" data-testid="current-read-section-less-sure">
          <div className={KICKER + " text-stone-500"}>What we're less sure about</div>
          {read.less_sure.map((p) => (
            <div key={p.theme} className="mt-3" data-testid={"current-read-less-sure-" + p.theme}>
              <p className={PROSE + " text-stone-600"}>{p.text}</p>
              <Notes item={p} id={"less-" + p.theme} />
              {p.evidence_lines?.length > 0 && (
                <Disclosure id={"less-" + p.theme} label="Why we think this" testId="current-read-evidence"><Lines lines={p.evidence_lines} /></Disclosure>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Options({ options, busy, chosen, onPick, idPrefix }) {
  return (
    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      {options.map((o) => (
        <button key={o.code} type="button" disabled={busy} onClick={() => onPick(o.code)} aria-pressed={chosen === o.code}
          data-testid={idPrefix + "-" + o.code}
          className={BTN + " border text-left " +
            (chosen === o.code ? "border-sky-800 bg-sky-50 text-sky-950" : "border-stone-300 bg-white text-stone-800 hover:border-sky-800 hover:text-sky-950")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function NaturalAnswer({ idPrefix, busy, onSend, initial = "", placeholder = "Or tell us in your own words…" }) {
  const [value, setValue] = useState(initial);
  const [listening, setListening] = useState(false);
  const usedSpeech = useRef(false);
  const recRef = useRef(null);
  const canSpeak = speechSupported();

  const listen = () => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new Ctor();
    rec.lang = "en-GB"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const said = Array.from(e.results).map((r) => r[0].transcript).join(" ").trim();
      usedSpeech.current = true;
      setValue((v) => (v ? v + " " : "") + said);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error("We couldn't hear that — you can type instead."); };
    recRef.current = rec; setListening(true); rec.start();
  };

  return (
    <div className="mt-6" data-testid={idPrefix + "-natural"}>
      <label className="sr-only" htmlFor={idPrefix + "-text"}>Answer in your own words</label>
      <textarea id={idPrefix + "-text"} data-testid={idPrefix + "-text"} rows={4} value={value}
        onChange={(e) => setValue(e.target.value)} placeholder={placeholder}
        className={"w-full rounded-2xl border border-stone-300 bg-white px-5 py-4 text-base sm:text-lg leading-relaxed text-stone-900 placeholder:text-stone-400 motion-safe:transition-colors focus:border-sky-700 " + FOCUS} />
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button type="button" disabled={busy || !value.trim()} data-testid={idPrefix + "-send"}
          onClick={() => onSend(value.trim(), usedSpeech.current ? "speech" : "text")}
          className={BTN + " bg-[#002147] font-semibold text-white hover:bg-[#0a2f5c]"}>
          {busy ? "Reading your answer…" : "Send answer"}
        </button>
        {canSpeak && (
          <button type="button" onClick={listen} aria-pressed={listening} data-testid={idPrefix + "-mic"}
            className={BTN + " border " + (listening ? "border-rose-400 bg-rose-50 text-rose-800" : "border-stone-300 text-stone-700 hover:border-sky-800")}>
            <Mic className="h-5 w-5" /> {listening ? "Listening… tap to stop" : "Speak instead"}
          </button>
        )}
      </div>
    </div>
  );
}

function InterpretationPanel({ data, idPrefix, busy, options, onConfirm, onPick, onEdit }) {
  const [correcting, setCorrecting] = useState(false);
  return (
    <div className="mt-6 border-l-4 border-sky-300 pl-5 sm:pl-6" data-testid={idPrefix + "-interpretation"}>
      <div className={KICKER + " text-sky-800"}>Here's what we heard · not saved yet</div>
      <p className="mt-2 font-editorial text-xl sm:text-2xl leading-snug text-stone-900">{data.interpretation}</p>
      {data.answer_text && <p className="mt-2 text-sm text-stone-500">You said: “{data.answer_text}”</p>}
      {data.needs_clarification ? (
        <>
          {data.clarification && <p className={"mt-3 " + PROSE}>{data.clarification}</p>}
          <Options options={data.candidate_readings || options} busy={busy} onPick={onPick} idPrefix={idPrefix + "-clarify"} />
        </>
      ) : (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button type="button" disabled={busy} onClick={onConfirm} data-testid={idPrefix + "-confirm"}
            className={BTN + " bg-[#002147] font-semibold text-white hover:bg-[#0a2f5c]"}>
            {busy ? "Saving…" : "Yes, that's right"}
          </button>
          <button type="button" disabled={busy} onClick={onEdit} data-testid={idPrefix + "-edit"}
            className={BTN + " border border-stone-300 text-stone-800 hover:border-sky-800"}>
            <Pencil className="h-4 w-4" /> Edit my answer
          </button>
          <button type="button" disabled={busy} onClick={() => setCorrecting((v) => !v)} aria-expanded={correcting} data-testid={idPrefix + "-correct"}
            className={BTN + " text-stone-600 underline-offset-4 hover:underline"}>
            Not quite — pick a different reading
          </button>
        </div>
      )}
      {correcting && <Options options={options} busy={busy} onPick={onPick} idPrefix={idPrefix + "-fix"} />}
    </div>
  );
}

function useAnswerLoop(initialDraft, post) {
  const toPanel = (d) => d && { interpretation: d.interpretation, needs_clarification: d.needs_clarification,
    candidate_readings: d.candidate_readings, derived_code: d.derived_code, answer_text: d.answer_text ?? d.text };
  const [panel, setPanel] = useState(toPanel(initialDraft));
  const [editing, setEditing] = useState(null);
  const sendText = async (text, mode) => {
    const d = await post({ text, input_mode: mode }, { silent: true });
    if (d) { setPanel(toPanel(d)); setEditing(null); }
  };
  const edit = () => { setEditing(panel?.answer_text || ""); setPanel(null); };
  return { panel, editing, sendText, edit };
}

function HypothesisCard({ h, onRespond, onResolved, primary }) {
  const [busy, setBusy] = useState(false);
  const post = async (body, opts) => { setBusy(true); const doc = await onRespond(h.hypothesis_id, body, opts); setBusy(false); return doc; };
  const loop = useAnswerLoop(h.draft, post);
  const pick = async (code) => { await post({ response_code: code }); };
  const accept = async () => { if (loop.panel?.derived_code) await post({ response_code: loop.panel.derived_code }); onResolved?.(); };
  const idp = "hypothesis-respond-" + h.hypothesis_id;
  return (
    <article className="min-w-0" data-testid={"hypothesis-card-" + h.hypothesis_id}>
      <h4 className={"font-editorial leading-tight text-stone-900 " + (primary ? "text-3xl sm:text-[2.5rem]" : "text-2xl")}>{h.title}</h4>
      <p className={"mt-4 " + PROSE} data-testid={"hypothesis-proposition-" + h.hypothesis_id}>{h.proposition}</p>
      {decidesBetween(h) && (
        <p className="mt-3 text-base leading-relaxed text-stone-500 break-words" data-testid={"hypothesis-decides-" + h.hypothesis_id}>{decidesBetween(h)}</p>
      )}
      {loop.panel ? (
        <InterpretationPanel data={loop.panel} idPrefix={idp} busy={busy} options={h.response_options}
          onConfirm={accept} onPick={pick} onEdit={loop.edit} />
      ) : (
        <>
          <Options options={h.response_options} busy={busy} onPick={pick} idPrefix={idp} />
          <NaturalAnswer key={loop.editing ?? "new"} idPrefix={idp} busy={busy} onSend={loop.sendText} initial={loop.editing || ""} />
        </>
      )}
      <Disclosure id={h.hypothesis_id} label="Why we're asking" testId="hypothesis-why">
        <p>{h.why_it_matters}</p>
        {h.decision_consequence && <p className="mt-1.5" data-testid={"hypothesis-consequence-" + h.hypothesis_id}>{h.decision_consequence}</p>}
        <Lines lines={h.evidence_summary} />
      </Disclosure>
    </article>
  );
}

function Hypotheses({ read, onRespond, onResolved }) {
  const primary = primaryQuestion(read);
  const others = otherQuestions(read);
  const [openMore, setOpenMore] = useState(false);
  if (!primary) return null;
  return (
    <div className="-mx-4 bg-sky-50/50 px-4 py-12 sm:-mx-10 sm:px-10 sm:py-16" data-testid="current-read-hypotheses">
      <div className="max-w-[44rem]">
        <h3 className={KICKER + " text-sky-900"}>One question for you</h3>
        <p className="mt-2 mb-8 text-base text-stone-500">Choose an option or answer in your own words. We'll show you what we heard before anything changes — it never changes your rankings or shortlist.</p>
        <HypothesisCard key={primary.hypothesis_id} h={primary} onRespond={onRespond} onResolved={onResolved} primary />
        {others.length > 0 && (
          <div className="mt-10">
            <button type="button" onClick={() => setOpenMore((v) => !v)} aria-expanded={openMore}
              className={"inline-flex min-h-10 items-center gap-1 text-base font-medium text-stone-600 hover:text-stone-900 " + FOCUS}
              data-testid="current-read-more-questions-toggle">
              More you could explore · {others.length}
              {openMore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {openMore && (
              <div className="mt-8 space-y-14" data-testid="current-read-more-questions">
                {others.map((h) => <HypothesisCard key={h.hypothesis_id} h={h} onRespond={onRespond} onResolved={onResolved} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Wins({ read }) {
  if (!hasWins(read)) return null;
  const w = read.what_wins;
  return (
    <div className="max-w-[44rem]" data-testid="current-read-what-wins">
      <h3 className="font-editorial text-2xl sm:text-3xl text-stone-900">What seems to win</h3>
      {w.summary && <p className={"mt-3 " + PROSE} data-testid="current-read-what-wins-summary">{w.summary}</p>}
      <ul className={"mt-3 space-y-1 " + PROSE}>
        {w.relations.map((r) => (
          <li key={r.winner + r.loser} data-testid="current-read-what-wins-relation">
            <span className="font-semibold">{r.winner}</span> beats {r.loser.toLowerCase()}
            <span className="text-sm text-stone-500"> · from {r.support} answer{r.support === 1 ? "" : "s"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Place({ a }) {
  return (
    <p className="mt-1 text-base text-stone-600">
      {a.university} · {a.city}, {a.country}
      {a.distance_miles != null && (
        <span data-testid={"alternative-distance-" + a.alternative_id}> · roughly {a.distance_miles} miles from {a.distance_from}</span>
      )}
    </p>
  );
}

function AlternativeCard({ a, onRespond, onResolved }) {
  const [askWhy, setAskWhy] = useState(false);
  const [reasons, setReasons] = useState(a.why_not || []);
  const [busy, setBusy] = useState(false);
  const post = async (body, opts) => {
    setBusy(true);
    const doc = await onRespond(a.hypothesis_id, { ...body, alternative_id: a.alternative_id }, opts);
    setBusy(false);
    return doc;
  };
  const loop = useAnswerLoop(a.draft, post);
  const send = async (code, why) => { await post({ response_code: code, why_not: why }); };
  const accept = async () => { if (loop.panel?.derived_code) await post({ response_code: loop.panel.derived_code }); onResolved?.(); };
  const toggle = (c) => setReasons((r) => (r.includes(c) ? r.filter((x) => x !== c) : [...r, c]));
  const idp = "alternative-respond-" + a.alternative_id;
  return (
    <article className="min-w-0 py-10 break-words [overflow-wrap:anywhere]" data-testid={"alternative-card-" + a.alternative_id}>
      <h4 className="font-editorial text-2xl sm:text-3xl leading-snug text-stone-900">{a.title}</h4>
      <Place a={a} />
      {a.theme_reason && <p className="mt-1 text-base text-stone-600" data-testid={"alternative-theme-reason-" + a.alternative_id}>{a.theme_reason}</p>}
      {a.outside_preference && (
        <p className="mt-2 text-base text-amber-900" data-testid={"alternative-outside-" + a.alternative_id}>Outside the area you said you'd prefer</p>
      )}
      <p className={"mt-4 " + PROSE} data-testid={"alternative-rationale-" + a.alternative_id}>{a.rationale}</p>
      {a.discipline_note && (
        <p className="mt-3 text-base italic text-stone-600" data-testid={"alternative-discipline-note-" + a.alternative_id}>{a.discipline_note}</p>
      )}
      {alternativeDetails(a).length > 0 && (
        <dl className="mt-4 space-y-3 text-base leading-relaxed">
          {alternativeDetails(a).map((d) => (
            <div key={d.key} data-testid={"alternative-" + d.key + "-" + a.alternative_id}>
              <dt className="font-semibold text-stone-800">{d.label}</dt>
              <dd className="text-stone-600">{d.text}</dd>
            </div>
          ))}
        </dl>
      )}
      {loop.panel ? (
        <InterpretationPanel data={loop.panel} idPrefix={idp} busy={busy} options={a.response_options}
          onConfirm={accept} onPick={(code) => send(code)} onEdit={loop.edit} />
      ) : (
        <>
          <Options options={a.response_options} busy={busy} chosen={askWhy ? "no" : null} idPrefix={idp}
            onPick={(code) => (code === "no" ? setAskWhy(true) : send(code))} />
          {!askWhy && <NaturalAnswer key={loop.editing ?? "new"} idPrefix={idp} busy={busy} onSend={loop.sendText}
            initial={loop.editing || ""} placeholder="What do you make of it? Say it however you like…" />}
        </>
      )}
      {askWhy && (
        <div className="mt-6" data-testid={"alternative-why-not-" + a.alternative_id}>
          <div className="text-base font-semibold text-stone-800">What puts you off?</div>
          <div className="mt-3 flex flex-wrap gap-3">
            {a.why_not_options.map((o) => (
              <button key={o.code} type="button" onClick={() => toggle(o.code)} aria-pressed={reasons.includes(o.code)}
                data-testid={"alternative-why-" + o.code + "-" + a.alternative_id}
                className={"min-h-11 rounded-full border px-4 py-2 text-base " + FOCUS + " " +
                  (reasons.includes(o.code) ? "border-sky-800 bg-sky-50 text-sky-950" : "border-stone-300 text-stone-700")}>
                {o.label}
              </button>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={async () => { await send("no", reasons); setAskWhy(false); }}
            className={BTN + " mt-5 bg-[#002147] font-semibold text-white"} data-testid={"alternative-why-submit-" + a.alternative_id}>
            {busy ? "Saving…" : "Send"}
          </button>
        </div>
      )}
    </article>
  );
}

function AnsweredAlternatives({ list, onShortlisted }) {
  const [open, setOpen] = useState(false);
  if (!list.length) return null;
  return (
    <div className="mt-6" data-testid="current-read-answered-alternatives">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={"inline-flex min-h-10 items-center gap-1 text-base font-medium text-stone-600 hover:text-stone-900 " + FOCUS}
        data-testid="current-read-answered-alternatives-toggle">
        Suggestions you've already answered · {list.length}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <ul className="mt-4 divide-y divide-stone-200 border-y border-stone-200">
          {list.map((a) => (
            <li key={a.alternative_id} className="py-5" data-testid={"alternative-card-" + a.alternative_id}>
              <div className="font-editorial text-xl text-stone-900">{a.title}</div>
              <Place a={a} />
              <p className={"mt-2 " + PROSE} data-testid={"alternative-state-" + a.alternative_id}>
                {a.heard || (a.response === "interesting" ? "You said you'd consider it." : "You said no" + (a.why_not?.length ? " — thanks for telling us why." : "."))}
              </p>
              {a.discipline_note && <p className="mt-1 text-sm italic text-stone-500">{a.discipline_note}</p>}
              {a.on_shortlist && <p className="mt-2 text-base text-emerald-800" data-testid={"alternative-on-shortlist-" + a.alternative_id}>It's on your shortlist now.</p>}
              {a.response === "interesting" && !a.on_shortlist && <ReturnActions alt={a} onShortlisted={onShortlisted} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Adding is only ever the student's explicit tap; the read is reloaded so the card shows "on your shortlist".
function useAddToShortlist(alt, onShortlisted) {
  const { addToShortlist } = useShortlistWriter();
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (busy) return false;
    setBusy(true);
    try {
      await addToShortlist({ university_id: alt.university_id, degree_id: alt.degree_id });
      toast.success(alt.title + " at " + alt.university + " is on your shortlist.");
      await onShortlisted?.();
      return true;
    } catch {
      toast.error("We couldn't add that to your shortlist. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { add, busy };
}

function ReturnActions({ alt, onShortlisted }) {
  const { add, busy } = useAddToShortlist(alt, onShortlisted);
  return (
    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-base" data-testid={"alternative-return-" + alt.alternative_id}>
      <Link to={"/degree/" + alt.degree_id} data-testid={"alternative-return-course-" + alt.alternative_id}
        className={"min-h-10 inline-flex items-center font-semibold text-[#002147] underline-offset-4 hover:underline " + FOCUS}>
        Look at the course page
      </Link>
      <button type="button" disabled={busy} onClick={add} data-testid={"alternative-return-shortlist-" + alt.alternative_id}
        className={"min-h-10 inline-flex items-center font-semibold text-[#002147] underline-offset-4 hover:underline disabled:opacity-60 " + FOCUS}>
        {busy ? "Adding…" : "Add it to your shortlist"}
      </button>
    </div>
  );
}

// After "I'd consider it": one explicit next step. Nothing is added to the shortlist unless the student taps it.
function ConsiderNext({ alt, onDone, onShortlisted }) {
  const { add: addAlt, busy } = useAddToShortlist(alt || {}, onShortlisted);
  if (!alt) return null;
  const add = async () => { await addAlt(); onDone(); };
  return (
    <div className="mt-6 border-l-4 border-emerald-300 pl-5" role="status" aria-live="polite" data-testid="consider-next">
      <p className={PROSE}>
        You said you'd consider <span className="font-semibold">{alt.title}</span> at {alt.university}. Do you want to:
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link to={"/degree/" + alt.degree_id} onClick={onDone} data-testid="consider-next-course-page"
          className={BTN + " border border-stone-300 text-stone-800 hover:border-sky-800"}>
          Look at the course page
        </Link>
        <button type="button" disabled={busy} onClick={add} data-testid="consider-next-shortlist"
          className={BTN + " bg-[#002147] font-semibold text-white hover:bg-[#0a2f5c]"}>
          {busy ? "Adding…" : "Add it to your shortlist"}
        </button>
        <button type="button" onClick={onDone} data-testid="consider-next-neither"
          className={BTN + " text-stone-600 underline-offset-4 hover:underline"}>
          Neither right now
        </button>
      </div>
    </div>
  );
}

function Discovery({ learning, area, broaden, onBroaden, more, onMore }) {
  const canMore = (learning.discovery?.more_available || 0) > 0 || more;
  const canWiden = area && (learning.outside_available > 0 || broaden);
  if (!learning.discovery_note && !canMore && !canWiden) return null;
  const link = "min-h-10 text-left text-base font-semibold text-[#4C1D95] underline-offset-4 hover:underline " + FOCUS;
  return (
    <div className="mt-4">
      {learning.discovery_note && (
        <p className="border-l-2 border-stone-300 pl-4 text-base leading-relaxed text-stone-700" role="status" data-testid="current-read-discovery-note">
          {learning.discovery_note}
        </p>
      )}
      <div className="mt-3 flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:gap-x-8">
        {canMore && (
          <button type="button" onClick={() => onMore(!more)} aria-pressed={more} data-testid="current-read-more-same-places" className={link}>
            {more ? "Back to one suggestion per place" : "Show more courses in the same places"}
          </button>
        )}
        {canWiden && (
          <button type="button" onClick={() => onBroaden(!broaden)} data-testid="current-read-broaden-toggle" className={link}>
            {broaden ? "Show options in " + area + " only" : "Show me options outside " + area + " too"}
          </button>
        )}
      </div>
    </div>
  );
}

function Alternatives({ read, onRespond, onResolved, broaden, onBroaden, more, onMore, followUp, onFollowUpDone }) {
  const list = alternatives(read);
  if (!list.length) return null;
  const learning = read?.learning || {};
  const area = learning.study_region?.area;
  const open = openAlternatives(read);
  return (
    <div className="max-w-[44rem]" data-testid="current-read-alternatives">
      <h3 className="font-editorial text-2xl sm:text-3xl text-stone-900">Have you thought of this?</h3>
      <p className="mt-2 text-base text-stone-500">
        Each one tests a read above. They aren't added to your shortlist.
        {area && !broaden ? " Because you'd prefer to study in " + area + ", we're showing options there first." : ""}
      </p>
      <Discovery learning={learning} area={area} broaden={broaden} onBroaden={onBroaden} more={more} onMore={onMore} />
      <ConsiderNext alt={followUp} onDone={onFollowUpDone} onShortlisted={onResolved} />
      <div className="mt-2 divide-y divide-stone-200">
        {open.map((a) => <AlternativeCard key={a.alternative_id} a={a} onRespond={onRespond} onResolved={onResolved} />)}
      </div>
      <AnsweredAlternatives list={answeredAlternatives(read)} onShortlisted={onResolved} />
    </div>
  );
}

function Challenge({ observations, onReacted }) {
  const [open, setOpen] = useState(false);
  if (!observations.length) return null;
  return (
    <div id="assumptions" className="max-w-[44rem] scroll-mt-24" data-testid="current-read-section-challenge">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={"inline-flex min-h-10 items-center gap-1 text-base font-medium text-stone-600 hover:text-stone-900 " + FOCUS} data-testid="current-read-challenge-toggle">
        Challenge a reading · {observations.length}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-4 grid gap-4" data-testid="current-read-challenge-content">
          {observations.map((obs) => <ObservationCard key={obs.observation_id} obs={obs} onReacted={onReacted} />)}
        </div>
      )}
    </div>
  );
}

function useCurrentRead(user) {
  const [read, setRead] = useState(null);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [broaden, setBroaden] = useState(false);
  const [more, setMore] = useState(false);
  const load = useCallback(async () => {
    const params = { ...(broaden ? { broaden: true } : {}), ...(more ? { more: "same_places" } : {}) };
    const [r, o] = await Promise.allSettled([
      api.get("/realisations/current-read", { params }),
      api.get("/realisations/observations/current"),
    ]);
    const next = r.status === "fulfilled" ? r.value.data : null;
    setRead(next);
    setObservations(o.status === "fulfilled" ? o.value.data?.observations || [] : []);
    setLoading(false);
    return next;
  }, [broaden, more]);
  useEffect(() => {
    if (user) load();
  }, [user, load]);
  return { read, setRead, observations, loading, load, broaden, setBroaden, more, setMore };
}

export default function CurrentRead({ user }) {
  const { read, setRead, observations, loading, load, broaden, setBroaden, more, setMore } = useCurrentRead(user);
  const [followUp, setFollowUp] = useState(null);

  // Returns the stored answer (so a card can show "here's what I heard"), and revises the portrait in place.
  const respond = async (id, body, opts = {}) => {
    const before = read?.overall_confidence;
    try {
      const res = await api.post(hypothesisResponsePath(id), body);
      const doc = res.data?.response || null;
      if (opts.silent) { load(); return doc; }  // proposed reading: keep the card, show the honest pending state
      const next = res.data?.current_read;
      if (next) setRead(next);
      await load();
      if (doc?.alternative_id && doc.status === "current" && doc.response_code === "interesting") {
        const alt = (read?.alternatives || []).find((a) => a.alternative_id === doc.alternative_id);
        if (alt) setFollowUp(alt);
      }
      if (doc && !doc.needs_clarification && doc.status !== "draft") {
        const change = confidenceChange(before, next?.overall_confidence);
        toast.success(doc.what_changed || ("Thanks — we've updated our read" + (change ? " (" + change + ")" : "") + "."));
      }
      return doc;
    } catch {
      toast.error("We couldn't save that answer. Please try again.");
      return null;
    }
  };

  if (loading) return <div className="mt-10 text-base text-stone-500" data-testid="current-read-loading">Loading your current read…</div>;
  if (!read?.ready) {
    return (
      <div className="mt-10 max-w-[44rem] border-l-2 border-stone-300 pl-5 text-base text-stone-600" data-testid="current-read-sparse">
        {read?.summary || "Shortlist a few degrees and WUA will start describing what your choices say about you."}
      </div>
    );
  }

  const pending = activeHypotheses(read).some((h) => h.draft) || alternatives(read).some((a) => a.draft);
  return (
    <section className="mt-12" aria-label="Our current read" data-testid="current-read">
      <div className={KICKER + " text-[#4C1D95]"}>Our current read</div>
      {pending && (
        <p className="mt-4 max-w-[44rem] border-l-4 border-sky-300 pl-4 text-base text-sky-950" role="status" data-testid="current-read-pending">
          We've heard your answer. Check what we heard below — once you confirm it, we'll update this read and your suggestions.
        </p>
      )}
      <div className="mt-5"><Lead read={read} /></div>
      <div className="mt-14 space-y-16 sm:space-y-20">
        <Portrait read={read} onRespond={respond} onResolved={load} />
        <Hypotheses read={read} onRespond={respond} onResolved={load} />
        <Learned read={read} />
        <Wins read={read} />
        <Alternatives read={read} onRespond={respond} onResolved={load} broaden={broaden} onBroaden={setBroaden} more={more} onMore={setMore}
          followUp={followUp} onFollowUpDone={() => setFollowUp(null)} />
        <Challenge observations={observations} onReacted={load} />
      </div>
      <p className="mt-16 max-w-[44rem] text-sm leading-relaxed text-stone-500" data-testid="current-read-explainer">
        This is a live interpretation of your current shortlist. Percentages are WUA's confidence in the evidence
        pattern, not statistical probabilities, and they move as you answer. Nothing here changes your rankings or
        shortlist, and nothing becomes "confirmed" unless you confirm it.
      </p>
    </section>
  );
}
